import './PromotePost.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRoute } from 'react-router5';

import {
  AnimatedChangeContent,
  Button,
  Form,
  FormItem,
  Header,
  Input,
  List,
  ListItem,
  Markdown,
  Placeholder,
  ScrollView,
  Select,
  Switch,
} from '$uikit';
import { useParseText, usePost, useTranslator } from '$hooks';
import { amountFractionDigits, formatAmount, formatInputAmount } from '$utils/number';
import { Loader } from '$shared/components';
import { Api } from '$api';
import { toastActions } from '$store/toast';
import { postsActions } from '$store/posts';
import { showModal, goBack } from '$navigation/helper';
import { adsActions } from '$store/ads';
import { LANGUAGE_MAP } from '$shared/constants/languages';
import { classNames } from '$utils';
import { CurrencyEnum } from '$store/models';

export interface PromotePostProps {
  postId: number;
}

export const PromotePost: FC<PromotePostProps> = ({ postId }) => {
  const { route } = useRoute();
  const t = useTranslator();
  const dispatch = useDispatch();
  const post = usePost(postId);
  const postText = useParseText(post?.text ?? '');

  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [price, setPrice] = useState('1');
  const [avgPrice, setAvgPrice] = useState(0.5);
  const [spendLimit, setSpendLimit] = useState('100');
  const [targetSubscribed, setTargetSubscribed] = useState(false);
  const [targetLang, setTargetLang] = useState('');
  const [minPrice, setMinPrice] = useState({ basic: 0.01, subscribed: 0.1 });
  const [showToConverted, setShowToConverted] = useState(true);
  const [currency, setCurrency] = useState(CurrencyEnum.TON);

  const digits = amountFractionDigits(currency);
  const avgPriceFormatted = useMemo(
    () => formatAmount(avgPrice, currency, { symbol: false }),
    [avgPrice, currency],
  );
  const minPriceFormatted = useMemo(
    () =>
      formatAmount(minPrice[targetSubscribed ? 'subscribed' : 'basic'], currency, {
        symbol: false,
      }),
    [minPrice, targetSubscribed, currency],
  );

  const handleLoad = useCallback(() => {
    setLoadingStatus('loading');
    Api.get(`/ads/post/${postId}/info`)
      .then((data: any) => {
        const adCurrency = (data.ads?.currency || CurrencyEnum.TON) as CurrencyEnum;
        const isPlace = adCurrency === CurrencyEnum.PLACE;
        const d = amountFractionDigits(adCurrency);

        if (data.post) {
          dispatch(postsActions.setPost({ id: data.post.id, updated: data.post }));
        }
        if (typeof data.balance === 'number')
          dispatch(adsActions.setAdsBalance(data.balance));
        if (typeof data.placeBalance === 'number') {
          dispatch(adsActions.setPlaceBalance(data.placeBalance));
        }

        setCurrency(adCurrency);
        setAvgPrice(isPlace ? data.avgPricePlace ?? data.avgPrice : data.avgPrice);
        setPrice(formatInputAmount(String(data.ads.price), d));
        setSpendLimit(formatInputAmount(String(data.ads.spendLimit), d));
        setTargetSubscribed(data.ads.targetSubscribed);
        setTargetLang(data.ads.targetLang);
        setShowToConverted(data.ads.showToConverted);
        setMinPrice({
          basic: isPlace ? data.minPricePlace ?? data.minPrice : data.minPrice,
          subscribed: isPlace
            ? data.minSubscriberPricePlace ?? data.minSubscriberPrice
            : data.minSubscriberPrice,
        });
        setLoadingStatus('');
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
        setLoadingStatus('failed');
      });
  }, [dispatch, postId]);

  useEffect(() => {
    handleLoad();
  }, [handleLoad]);

  const handleUpdateAvgPrice = useCallback(
    (isSubscribed: boolean, lang: string) => {
      Api.post('/ads/price/info', {
        targetSubscribed: isSubscribed,
        targetLang: lang === 'all' ? '' : lang,
      }).then((data: any) => {
        if (currency === CurrencyEnum.PLACE) {
          const placeMin = data.minPricePlace ?? data.minPrice;
          setAvgPrice(data.avgPricePlace ?? data.avgPrice);
          setMinPrice({ basic: placeMin, subscribed: placeMin });
        } else {
          setAvgPrice(data.avgPrice);
          setMinPrice({ basic: data.minPrice, subscribed: data.minSubscriberPrice });
        }
      });
    },
    [currency],
  );

  const handleSave = useCallback(() => {
    dispatch(toastActions.setToastLoading());
    Api.post(`/ads/${postId}/save`, {
      price: +price,
      spendLimit: +spendLimit,
      targetSubscribed,
      targetLang: targetLang === 'all' ? '' : targetLang,
      showToConverted,
    })
      .then((data: any) => {
        dispatch(toastActions.hideToast());
        if (data.ad) dispatch(adsActions.setAd(data.ad));
        if (route.params.target === 'post') showModal(`ad${data.ad.id}`, true);
        else goBack();
      })
      .catch((e) => dispatch(toastActions.setToastFail(e.message)));
  }, [
    postId,
    price,
    spendLimit,
    targetSubscribed,
    targetLang,
    showToConverted,
    dispatch,
    route.params.target,
  ]);

  if (loadingStatus === 'loading') {
    return (
      <>
        <Header showBackButton>{t('promote_post_title')}</Header>
        <Loader isWrapped />
      </>
    );
  }

  if (loadingStatus === 'failed') {
    return (
      <>
        <Header showBackButton>{t('promote_post_title')}</Header>
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={handleLoad}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      </>
    );
  }

  const currencyLabel = currency.toUpperCase();

  return (
    <>
      <Header showBackButton>{t('promote_post_title')}</Header>
      <ScrollView>
        <div className="PromotePost__post_info">
          <List>
            <ListItem
              description={
                postText.length > 0
                  ? postText.substring(0, 100)
                  : t('attach_type_post_description')
              }
              descriptionSingleLine
            >
              {t('promote_post_source')}
            </ListItem>
          </List>
        </div>
        <Form>
          <FormItem
            title={t('promote_post_price')}
            description={
              <div
                className={classNames({
                  PromotePost__price_info: true,
                  low: avgPrice > +price,
                })}
              >
                <AnimatedChangeContent animateKey={avgPriceFormatted}>
                  <Markdown>
                    {t('promote_post_price_description', {
                      price: avgPriceFormatted,
                      minPrice: minPriceFormatted,
                      currency: currencyLabel,
                    })}
                  </Markdown>
                </AnimatedChangeContent>
              </div>
            }
          >
            <Input
              type="price"
              currency={currency}
              currencyAlign="end"
              value={price}
              onChange={({ target }) => setPrice(formatInputAmount(target.value, digits))}
            />
          </FormItem>
          <FormItem title={t('promote_post_target_subscribed')}>
            <Select
              value={targetSubscribed ? 'subscribed' : 'all'}
              onChange={({ target }) => {
                const isSubscribed = target.value === 'subscribed';
                setTargetSubscribed(isSubscribed);
                handleUpdateAvgPrice(isSubscribed, targetLang);
              }}
            >
              <option value="all">{t('promote_post_target_subscribed_off')}</option>
              <option value="subscribed">{t('promote_post_target_subscribed_on')}</option>
            </Select>
          </FormItem>
          <FormItem title={t('promote_post_target_lang')}>
            <Select
              value={targetLang}
              onChange={({ target }) => {
                setTargetLang(target.value);
                handleUpdateAvgPrice(targetSubscribed, target.value);
              }}
            >
              {['all', ...Object.keys(LANGUAGE_MAP)].map((lang) => (
                <option key={lang} value={lang}>
                  {lang === 'all' ? t('promote_post_target_any') : LANGUAGE_MAP[lang]}
                </option>
              ))}
            </Select>
          </FormItem>
          <FormItem
            title={t('promote_post_spend_limit')}
            description={t('promote_post_spend_limit_description', {
              currency: currencyLabel,
            })}
          >
            <Input
              type="price"
              currency={currency}
              currencyAlign="end"
              value={spendLimit}
              onChange={({ target }) =>
                setSpendLimit(formatInputAmount(target.value, digits))
              }
            />
          </FormItem>
          <FormItem description={t('promote_post_repeat_show_caption')}>
            <List>
              <ListItem
                after={<Switch checked={showToConverted} />}
                onClick={() => setShowToConverted(!showToConverted)}
              >
                {t('promote_post_repeat_show')}
              </ListItem>
            </List>
          </FormItem>
          <FormItem
            description={
              <>
                {t('promote_post_terms_pref')}{' '}
                <a href="/ads_terms.html" target="_blank" rel="noreferrer">
                  {t('promote_post_terms_link')}
                </a>
                .
              </>
            }
          >
            <Button stretched onClick={handleSave}>
              {t('save')}
            </Button>
          </FormItem>
        </Form>
      </ScrollView>
    </>
  );
};
